import { getBespokeModuleSupport } from './teamMetadata';

export const REQUIRED_RESTRICTED_CARD_IDS = Object.freeze(['manageMembership', 'demographics']);

export const RESTRICTABLE_DASHBOARD_CARDS = Object.freeze([
  { id: 'events', label: 'Events' },
  { id: 'courses', label: 'Courses' },
  { id: 'documents', label: 'Documents' },
  { id: 'profileFields', label: 'Profile Details' },
  { id: 'mentoring', label: 'Mentoring' },
  { id: 'rideReports', label: 'Ride Reports' },
  { id: 'rideDeclarations', label: 'Ride Declaration' },
  { id: 'messageBoards', label: 'Message Boards' },
  { id: 'publicLinks', label: 'Public Links' },
  { id: 'admins', label: 'Admins' },
  { id: 'address', label: 'Address' },
  { id: 'myTags', label: 'My Tags' },
  { id: 'boltons', label: 'Bolt-ons' },
  { id: 'boatBooking', label: 'Boat Booking' },
  { id: 'boatMaintenance', label: 'Boat Maintenance' },
  { id: 'myOutings', label: 'My Outings' },
  { id: 'sailingBoatBooking', label: 'Sailing Boat Booking' },
  { id: 'mySailingOutings', label: 'My Club-led Outings' },
  { id: 'sailingCrewFinder', label: 'Crew Finder' },
  { id: 'dutyRoster', label: 'Duty Roster' },
  { id: 'moorings', label: 'Moorings' },
]);

const RESTRICTABLE_CARD_ID_SET = new Set([
  ...REQUIRED_RESTRICTED_CARD_IDS,
  ...RESTRICTABLE_DASHBOARD_CARDS.map((card) => card.id),
]);

const IAM_ROADSMART_CARD_IDS = new Set(['rideReports', 'rideDeclarations']);
const ROWING_CARD_IDS = new Set(['boatBooking', 'myOutings']);
const SAILING_CARD_IDS = new Set(['sailingBoatBooking', 'mySailingOutings', 'sailingCrewFinder', 'dutyRoster', 'moorings']);

export function getRestrictableDashboardCards(metadata = {}) {
  const support = getBespokeModuleSupport(metadata);
  return RESTRICTABLE_DASHBOARD_CARDS.filter((card) => {
    if (IAM_ROADSMART_CARD_IDS.has(card.id)) return support.iamRoadSmart;
    if (ROWING_CARD_IDS.has(card.id)) return support.rowing;
    if (SAILING_CARD_IDS.has(card.id)) return support.sailing;
    if (card.id === 'boatMaintenance') return support.rowing || support.sailing;
    return true;
  });
}

export function normalizeRestrictedCardIds(value = []) {
  return Array.from(new Set(
    (Array.isArray(value) ? value : [])
      .map((cardId) => (typeof cardId === 'string' ? cardId.trim() : ''))
      .filter((cardId) => cardId && RESTRICTABLE_CARD_ID_SET.has(cardId))
  ));
}

export function buildCardRestriction({
  enabled = false,
  allowedCardIds = [],
  alwaysAllowedCardIds = REQUIRED_RESTRICTED_CARD_IDS,
  reason = '',
} = {}) {
  return {
    enabled: enabled === true,
    allowedCardIds: normalizeRestrictedCardIds(allowedCardIds),
    alwaysAllowedCardIds: normalizeRestrictedCardIds(alwaysAllowedCardIds),
    reason: typeof reason === 'string' ? reason.trim() : '',
  };
}

export function isCardAllowedByRestriction(cardId = '', restriction = null) {
  if (!restriction?.enabled) return true;
  const normalizedCardId = typeof cardId === 'string' ? cardId.trim() : '';
  if (!normalizedCardId) return false;
  const alwaysAllowed = new Set(normalizeRestrictedCardIds(restriction.alwaysAllowedCardIds));
  if (alwaysAllowed.has(normalizedCardId)) return true;
  const allowed = new Set(normalizeRestrictedCardIds(restriction.allowedCardIds));
  return allowed.has(normalizedCardId);
}

export function filterCardsByRestriction(cards = [], restriction = null) {
  if (!restriction?.enabled) return cards;
  return (Array.isArray(cards) ? cards : []).filter((card) => isCardAllowedByRestriction(card?.id, restriction));
}
